import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, User, Loader2, Mail, Play, Grid3x3, Folder, Settings, Pencil, Share2, TrendingUp, Lock, Pin, UploadCloud, Send, X, Check, Sparkles, Download, Trash2, Plus, EyeOff, Link as LinkIcon } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { optimizeImg, mediaSrc } from "@/lib/img";
import { LOGO_MARK } from "@/lib/brand";
import BottomNav from "@/components/BottomNav";
import PostComposer from "@/components/PostComposer";
import LinkCard from "@/components/LinkCard";

const DM_MAX = 10;

export const NOSEY_AVATARS = [
  "https://static.prod-images.emergentagent.com/jobs/4243698c-8211-43cc-9945-b3328747a619/images/dd849744444538ecd0a042321cf71ab1000a81a5f0cd6bc97b23058305fc615b.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/4243698c-8211-43cc-9945-b3328747a619/images/59e98ca6ad7564f6982427b05f4d10418832af539574eaca7ef8a0a51f0196bb.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/4243698c-8211-43cc-9945-b3328747a619/images/d68461278c38c28b36355771f74d7b59e620e02f3c0c5cee2720f77ef678aa0d.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/4243698c-8211-43cc-9945-b3328747a619/images/cd8237ea68b73b5894710050507de12fcbd317516bf735e48e7a2d81c5936e55.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/4243698c-8211-43cc-9945-b3328747a619/images/7d8c44e60a2c75945f1fbdf156e46689a591dccd13c875c910dc4e5da96b786b.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/4243698c-8211-43cc-9945-b3328747a619/images/f55f8812d43e875bf392a463a8915e3b41a63fb33aa31b446681fcd470371192.jpeg",
];

function Crown({ glowing }) {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" className="shrink-0" style={glowing ? { filter: "drop-shadow(0 0 6px #22e06b)" } : {}} data-testid="profile-crown">
      <path d="M3 7l4 4 5-7 5 7 4-4-2 12H5L3 7z" fill={glowing ? "#22e06b" : "#5b6152"} stroke={glowing ? "#22e06b" : "#6b7160"} strokeWidth="0.5" />
    </svg>
  );
}

export default function Profile() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [following, setFollowing] = useState(false);
  const [requested, setRequested] = useState(false);
  const [tab, setTab] = useState(sp.get("tab") === "folder" ? "folder" : "posts");
  const [creations, setCreations] = useState(null);
  const [priv, setPriv] = useState(false);
  const [edit, setEdit] = useState(false);
  const [viewC, setViewC] = useState(null);
  const [viewP, setViewP] = useState(null);
  const [compose, setCompose] = useState(false);

  const load = useCallback(async () => {
    const id = uid || user?.id;
    if (!id) return;
    try {
      const { data } = await api.get(`/users/${id}/profile`);
      setData(data); setFollowing(data.is_following); setRequested(!!data.requested); setPriv(data.user.is_private);
    } catch { toast.error("Profile not found"); navigate("/home"); }
  }, [uid, user, navigate]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab === "folder" && data?.is_me && creations === null) {
      api.get("/me/creations").then(({ data }) => setCreations(data)).catch(() => setCreations([]));
    }
  }, [tab, data, creations]);

  const follow = async () => {
    try {
      const { data: r } = await api.post(`/users/${data.user.id}/follow`);
      setFollowing(r.following); setRequested(!!r.requested);
      if (r.requested) toast.success("Follow request sent");
    } catch { toast.error("Couldn't update"); }
  };
  const togglePrivacy = async () => {
    const next = !priv; setPriv(next);
    try { await api.post("/me/privacy", { is_private: next }); toast.success(next ? "Profile is now private" : "Profile is now public"); }
    catch { setPriv(!next); toast.error("Couldn't update privacy"); }
  };
  const share = async () => {
    const link = `nosey.ai/@${data.user.username}`;
    try { await navigator.clipboard.writeText(link); toast.success(`Copied ${link}`); } catch { toast(link); }
  };
  const publish = async (c) => {
    try {
      const { data: post } = await api.post("/posts/publish", { creation_type: c.creation_type, creation_id: c.id, origin_url: window.location.origin });
      toast.success("Published to your feed 🎉");
      setCreations((list) => list.map((x) => (x.id === c.id ? { ...x, published_post_id: post.id } : x)));
      setViewC((v) => (v && v.id === c.id ? { ...v, published_post_id: post.id } : v));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
  };
  const unpublish = async (c) => {
    if (!c.published_post_id) return;
    try {
      await api.delete(`/posts/${c.published_post_id}`);
      toast.success("Removed from your feed");
      setCreations((list) => list.map((x) => (x.id === c.id ? { ...x, published_post_id: null } : x)));
      setViewC((v) => (v && v.id === c.id ? { ...v, published_post_id: null } : v));
      load();
    } catch { toast.error("Couldn't unpublish"); }
  };
  const deleteCreation = async (c) => {
    if (!window.confirm("Delete this creation permanently?")) return;
    try {
      await api.delete(c.creation_type === "video" ? `/videos/${c.id}` : `/images/${c.id}`);
      toast.success("Deleted");
      setCreations((list) => list.filter((x) => x.id !== c.id));
      setViewC(null);
    } catch { toast.error("Delete failed"); }
  };
  const shareCreation = async (c) => {
    if (!c.published_post_id) { toast.error("Publish it first to get a shareable link"); return; }
    const url = `${window.location.origin}/post/${c.published_post_id}`;
    try {
      if (navigator.share) await navigator.share({ url, title: "Nosey AI" });
      else { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    } catch { /* cancelled */ }
  };
  const downloadCreation = async (c) => {
    try {
      const src = c.creation_type === "video" ? c.url : c.thumb;
      const res = await fetch(src);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `nosey_${c.id.slice(0, 6)}.${c.creation_type === "video" ? "mp4" : "png"}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      toast.success("Download started");
    } catch { toast.error("Download failed"); }
  };
  const togglePin = async (postId) => {
    try { const { data: r } = await api.post(`/posts/${postId}/pin`); toast.success(r.pinned ? "Pinned" : "Unpinned"); load(); }
    catch { toast.error("Couldn't pin"); }
  };
  const deletePost = async (p) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try { await api.delete(`/posts/${p.id}`); toast.success("Deleted"); setViewP(null); load(); }
    catch { toast.error("Delete failed"); }
  };
  const sharePost = async (p) => {
    const url = `${window.location.origin}/post/${p.id}`;
    try { if (navigator.share) await navigator.share({ url, title: "Nosey AI" }); else { await navigator.clipboard.writeText(url); toast.success("Link copied"); } }
    catch { /* cancelled */ }
  };
  const togglePostFeed = async (p) => {
    const on = !(p.to_feed !== false);
    try {
      await api.post(`/posts/${p.id}/feed`, { on });
      toast.success(on ? "Shared to feed · 1 coin" : "Removed from feed");
      if (on) refreshUser();
      setViewP((v) => (v ? { ...v, to_feed: on } : v)); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update"); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-ink-950"><Loader2 className="animate-spin text-gold" size={26} /></div>;
  const u = data.user;
  const isPro = u.plan && u.plan !== "free";

  return (
    <div className="min-h-screen bg-ink-950 text-[#F0E9DD] pb-24 overflow-x-hidden">
      <header className="sticky top-0 z-30 bg-ink-950/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between gap-2">
          <button onClick={() => (uid ? navigate(-1) : navigate("/home"))} className="flex items-center gap-2">
            {uid ? <ArrowLeft size={22} className="text-zinc-300" /> : <img src={LOGO_MARK} alt="" className="w-7 h-7" />}
            {!uid && <span className="font-heading font-bold text-lg">nosey<span className="text-gold"> ai</span></span>}
          </button>
          <div className="flex items-center gap-2">
            {data.is_me && <button onClick={() => navigate("/settings")} data-testid="profile-settings" className="border border-gold/30 bg-[#1a140d] rounded-lg p-2"><Settings size={16} className="text-gold" /></button>}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-4">
        <div className="flex gap-4">
          <button onClick={() => data.is_me && setEdit(true)} className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden bg-ink-800 border-2 border-gold/50 shrink-0" data-testid="profile-avatar">
            {u.avatar_url ? <img src={optimizeImg(u.avatar_url, 240, 75)} alt="" decoding="async" className="w-full h-full object-cover" /> : <User size={34} className="m-auto mt-8 text-zinc-500" />}
          </button>
          <div className="min-w-0 flex-1 pt-1">
            <div className="flex items-center gap-2">
              <h1 className="font-heading font-black text-2xl truncate" data-testid="profile-username">{u.username}</h1>
              <Crown glowing={isPro} />
            </div>
            <p className="text-sm text-zinc-300 mt-1 line-clamp-3" data-testid="profile-bio">{u.bio || (data.is_me ? "Add a bio in Edit profile." : "")}</p>
            {u.link?.url && <a href={u.link.url} target="_blank" rel="noopener noreferrer" data-testid="profile-link" className="inline-flex items-center gap-1 mt-1.5 text-sm text-gold hover:underline max-w-full truncate"><LinkIcon size={13} className="shrink-0" />{u.link.label || u.link.url}</a>}
          </div>
        </div>

        <div className="grid grid-cols-3 mt-5 text-center">
          {[["posts", data.posts.length], ["followers", u.followers_count], ["following", u.following_count]].map(([l, n], i) => (
            <div key={l} className={i < 2 ? "border-r border-white/10" : ""}>
              <div className="font-heading font-black text-2xl">{n}</div><div className="text-xs text-zinc-500">{l}</div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-3 rounded-xl border border-white/10 bg-ink-900/60 px-4 py-3" data-testid="profile-views">
          <TrendingUp size={18} className="text-gold" />
          <span className="text-sm text-zinc-300 flex-1"><b>{data.views_30d || 0}</b> views in the last 30 days</span>
        </div>

        {data.is_me ? (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <button onClick={() => setEdit(true)} data-testid="edit-profile-btn" className="flex items-center justify-center gap-2 border border-gold/40 rounded-xl py-3 font-heading font-bold text-sm hover:bg-[#1a140d]"><Pencil size={16} className="text-gold" /> Edit profile</button>
            <button onClick={share} data-testid="share-profile-btn" className="flex items-center justify-center gap-2 border border-gold/40 rounded-xl py-3 font-heading font-bold text-sm hover:bg-[#1a140d]"><Share2 size={16} className="text-gold" /> Share profile</button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <button onClick={follow} data-testid="profile-follow" className={`rounded-xl py-3 font-heading font-bold text-sm border ${following ? "border-zinc-700 text-zinc-300" : requested ? "border-zinc-700 text-zinc-400" : "bg-gold text-ink-950 border-gold"}`}>{following ? "Following" : requested ? "Requested" : "Follow"}</button>
            <button onClick={() => navigate(`/messages?to=${u.id}`)} data-testid="profile-message" className="rounded-xl py-3 font-heading font-bold text-sm border border-zinc-700 flex items-center justify-center gap-2"><Mail size={16} /> Message</button>
          </div>
        )}

        <div className="flex mt-6 border-b border-white/10">
          <button onClick={() => setTab("posts")} data-testid="tab-posts" className={`flex-1 flex justify-center py-3 ${tab === "posts" ? "text-gold border-b-2 border-gold" : "text-zinc-500"}`}><Grid3x3 size={22} /></button>
          {data.is_me && <button onClick={() => setTab("folder")} data-testid="tab-folder" className={`flex-1 flex justify-center py-3 ${tab === "folder" ? "text-gold border-b-2 border-gold" : "text-zinc-500"}`}><Folder size={22} /></button>}
        </div>

        {tab === "posts" ? (
          data.locked ? (
            <div className="py-20 flex flex-col items-center text-center" data-testid="profile-locked"><Lock size={34} className="text-zinc-600 mb-3" /><p className="text-zinc-400">This profile is private.</p></div>
          ) : data.posts.length === 0 ? (
            <div className="py-20 flex flex-col items-center text-center" data-testid="profile-empty"><Sparkles size={34} className="text-zinc-700 mb-3" /><p className="text-zinc-400">{data.is_me ? "Publish a creation from the Folder tab." : "No posts yet."}</p></div>
          ) : (
            <div className="grid grid-cols-3 gap-1 mt-1" data-testid="profile-posts">
              {data.posts.map((p) => (
                <button key={p.id} onClick={() => setViewP(p)} data-testid={`post-${p.id}`} className="relative aspect-square overflow-hidden bg-ink-900 group">
                  {p.media_type === "video" ? <><video src={p.media_url} muted preload="auto" className="w-full h-full object-cover" /><Play size={18} className="absolute top-1.5 right-1.5 text-white/90" /></>
                    : p.media_type === "link" ? (
                      p.link?.image ? <><img src={mediaSrc(p.link.image)} alt="" decoding="async" className="w-full h-full object-cover" /><LinkIcon size={16} className="absolute top-1.5 right-1.5 text-white/90" /></>
                        : <div className="w-full h-full bg-gradient-to-br from-ink-800 to-ink-900 flex items-center justify-center"><LinkIcon size={22} className="text-zinc-600" /></div>
                    )
                    : <img src={optimizeImg(p.thumb_url || p.media_url, 400, 68)} alt="" decoding="async" className="w-full h-full object-cover" />}
                  {p.to_feed === false && data.is_me && <span className="absolute bottom-1 left-1 bg-black/60 rounded px-1.5 py-0.5 text-[9px] font-bold text-zinc-300">Profile only</span>}
                </button>
              ))}
            </div>
          )
        ) : (
          creations === null ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-gold" size={24} /></div>
          : creations.length === 0 ? <div className="py-20 text-center text-zinc-500" data-testid="folder-empty">No private creations yet. Make something with +.</div>
          : <div className="grid grid-cols-3 gap-1 mt-1" data-testid="folder-grid">
              {creations.map((c) => (
                <button key={`${c.creation_type}-${c.id}`} onClick={() => setViewC(c)} data-testid={`creation-${c.id}`} className="relative aspect-square overflow-hidden bg-ink-900">
                  {c.creation_type === "video" ? <><video src={c.url} muted preload="auto" className="w-full h-full object-cover" /><Play size={16} className="absolute top-1.5 right-1.5 text-white/90" /></>
                    : <img src={c.thumb} alt="" decoding="async" className="w-full h-full object-cover" />}
                  {c.published_post_id && <span className="absolute bottom-1 left-1 bg-green-600/80 rounded px-1.5 py-0.5 text-[9px] font-bold">Posted</span>}
                </button>
              ))}
            </div>
        )}
      </main>

      {viewP && <PostDetailModal p={viewP} isMe={data.is_me} onClose={() => setViewP(null)} onDelete={deletePost} onShare={sharePost} onToggleFeed={togglePostFeed} />}
      {viewC && <CreationMiniModal c={viewC} onClose={() => setViewC(null)}
        onPublish={publish} onUnpublish={unpublish} onDelete={deleteCreation} onShare={shareCreation} onDownload={downloadCreation} />}
      {compose && <PostComposer onClose={() => setCompose(false)} onPosted={() => { setCompose(false); setCreations(null); setTab("posts"); load(); }} />}
      {data.is_me && (
        <button onClick={() => setCompose(true)} data-testid="profile-compose" className="fixed bottom-24 right-5 z-40 w-14 h-14 rounded-2xl bg-[#22c55e] text-ink-950 flex items-center justify-center shadow-2xl shadow-black/50 hover:bg-[#26d466] transition-colors"><Plus size={26} /></button>
      )}

      {edit && <EditProfileModal user={u} onClose={() => setEdit(false)} onSaved={() => { setEdit(false); refreshUser(); load(); }} />}
      <BottomNav />
    </div>
  );
}

function EditProfileModal({ user, onClose, onSaved }) {
  const { user: me } = useAuth();
  const [username, setUsername] = useState(user.username || "");
  const [bio, setBio] = useState(user.bio || "");
  const [avatar, setAvatar] = useState(user.avatar_url || "");
  const [isPrivate, setIsPrivate] = useState(!!user.is_private);
  const [dmEnabled, setDmEnabled] = useState(!!me?.dm_charge_enabled);
  const [dmPrice, setDmPrice] = useState(me?.dm_price_cents ?? 10);
  const [linkLabel, setLinkLabel] = useState(user.link?.label || "");
  const [linkUrl, setLinkUrl] = useState(user.link?.url || "");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const avatarChanged = avatar !== (user.avatar_url || "");
  useEffect(() => { const h = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
      const { data } = await api.post("/uploads/image", { image_base64: b64, origin_url: window.location.origin });
      setAvatar(data.url);
    } catch { toast.error("Upload failed"); } finally { setUploading(false); e.target.value = ""; }
  };
  const save = async () => {
    setBusy(true);
    const payload = { username, bio, is_private: isPrivate, dm_charge_enabled: dmEnabled, dm_price_cents: Number(dmPrice),
      link: linkUrl.trim() ? { label: linkLabel.trim(), url: linkUrl.trim() } : null };
    if (avatarChanged) payload.avatar_url = avatar;
    try { await api.put("/me/profile", payload); toast.success("Profile updated"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="edit-profile-modal">
      <div className="absolute inset-0 bg-black/75" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-ink-900 border-t sm:border border-zinc-800 sm:rounded-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 sticky top-0 bg-ink-900 z-10">
          <span className="font-heading font-bold">Edit profile</span>
          <button onClick={onClose} data-testid="edit-profile-close" className="text-zinc-400"><X size={20} /></button>
        </div>
        <div className="p-4 space-y-5">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-20 h-20 rounded-2xl overflow-hidden bg-ink-800 border border-gold/40 shrink-0">{avatar ? <img src={avatar} alt="" className="w-full h-full object-cover" /> : <User size={26} className="m-auto mt-7 text-zinc-500" />}</div>
              <label className="flex items-center gap-2 border border-gold/40 rounded-lg px-3 py-2 text-sm cursor-pointer hover:bg-[#1a140d]" data-testid="edit-avatar-upload">
                {uploading ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} className="text-gold" />} Upload
                <input type="file" accept="image/*" onChange={upload} className="hidden" />
              </label>
            </div>
            <p className="text-[11px] text-zinc-500 mt-2">Or pick a Nosey icon. Profile image can be changed once every 14 days.</p>
            <div className="flex gap-2 mt-2 overflow-x-auto no-scrollbar pb-1">
              {NOSEY_AVATARS.map((a) => (
                <button key={a} onClick={() => setAvatar(a)} data-testid="edit-avatar-preset" className={`relative w-14 h-14 rounded-xl overflow-hidden border-2 shrink-0 ${avatar === a ? "border-gold" : "border-transparent"}`}>
                  <img src={a} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-500">Username</label>
            <div className="flex items-center bg-ink-950 border border-zinc-800 rounded-lg mt-1 focus-within:border-gold">
              <span className="pl-3 text-zinc-500">@</span>
              <input value={username} onChange={(e) => setUsername(e.target.value)} data-testid="edit-username" className="flex-1 bg-transparent px-2 py-2.5 text-sm focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-500">Bio</label>
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} data-testid="edit-bio" rows={3} maxLength={300}
              className="w-full bg-ink-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm mt-1 focus:border-gold focus:outline-none resize-none" />
          </div>
          <div>
            <label className="text-xs text-zinc-500">Link (shown on your profile)</label>
            <input value={linkLabel} onChange={(e) => setLinkLabel(e.target.value)} data-testid="edit-link-label" placeholder="Label (e.g. My website)"
              className="w-full bg-ink-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm mt-1 focus:border-gold focus:outline-none" />
            <input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} data-testid="edit-link-url" placeholder="https://…"
              className="w-full bg-ink-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm mt-2 focus:border-gold focus:outline-none" />
          </div>

          {/* privacy */}
          <div className="flex items-center justify-between border-t border-white/10 pt-4">
            <div><div className="text-sm font-heading font-semibold">Private profile</div><div className="text-[11px] text-zinc-500">Only followers see your profile. Published posts still show in the feed.</div></div>
            <button onClick={() => setIsPrivate((v) => !v)} data-testid="edit-privacy-toggle" className={`w-11 h-6 rounded-full flex items-center transition-colors shrink-0 ${isPrivate ? "" : "bg-zinc-700"}`} style={isPrivate ? { backgroundColor: "#22e06b" } : {}}>
              <span className={`w-5 h-5 bg-white rounded-full transition-transform ${isPrivate ? "translate-x-5" : "translate-x-0.5"}`} /></button>
          </div>

          {/* DM charging */}
          <div className="border-t border-white/10 pt-4">
            <div className="flex items-center justify-between">
              <div><div className="text-sm font-heading font-semibold">Charge for DMs</div><div className="text-[11px] text-zinc-500">People pay coins to message you (max {DM_MAX} coins).</div></div>
              <button onClick={() => setDmEnabled((v) => !v)} data-testid="edit-dm-toggle" className={`w-11 h-6 rounded-full flex items-center transition-colors shrink-0 ${dmEnabled ? "bg-gold" : "bg-zinc-700"}`}>
                <span className={`w-5 h-5 bg-white rounded-full transition-transform ${dmEnabled ? "translate-x-5" : "translate-x-0.5"}`} /></button>
            </div>
            {dmEnabled && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs text-zinc-400 mb-1"><span>DM price</span><span className="font-mono text-gold">{dmPrice}¢ · {dmPrice} coins</span></div>
                <input type="range" min="0" max={DM_MAX} step="1" value={dmPrice} onChange={(e) => setDmPrice(e.target.value)} data-testid="edit-dm-price" className="w-full accent-[#c68a5b]" />
              </div>
            )}
          </div>

          <button onClick={save} disabled={busy} data-testid="edit-save" className="w-full rounded-lg bg-gold text-ink-950 font-heading font-black py-3 disabled:opacity-60 flex items-center justify-center gap-2">
            {busy ? <Loader2 size={18} className="animate-spin" /> : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}


function CreationMiniModal({ c, onClose, onPublish, onUnpublish, onDelete, onShare, onDownload }) {
  const isVideo = c.creation_type === "video";
  const published = !!c.published_post_id;
  useEffect(() => { const h = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);
  const Action = ({ icon: Icon, label, onClick, testid, danger }) => (
    <button onClick={onClick} data-testid={testid}
      className={`flex flex-col items-center gap-1 flex-1 min-w-[60px] py-2.5 rounded-lg border transition-colors ${danger ? "border-zinc-800 text-zinc-400 hover:border-red-500 hover:text-red-400" : "border-zinc-800 text-zinc-300 hover:border-gold hover:text-gold"}`}>
      <Icon size={17} /><span className="text-[10px] font-heading">{label}</span>
    </button>
  );
  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="creation-mini-modal">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-ink-900 border-t sm:border border-zinc-800 sm:rounded-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 sticky top-0 bg-ink-900 z-10">
          <span className="font-heading font-bold">Creation</span>
          <button onClick={onClose} data-testid="creation-mini-close" className="text-zinc-400"><X size={20} /></button>
        </div>
        <div className="w-full bg-black flex items-center justify-center max-h-[55vh]">
          {isVideo ? <video src={c.url} controls autoPlay loop playsInline className="w-full max-h-[55vh] object-contain" data-testid="creation-mini-video" />
            : <img src={c.thumb} alt="" className="w-full max-h-[55vh] object-contain" data-testid="creation-mini-image" />}
        </div>
        <div className="p-4 space-y-4">
          <div className="flex flex-wrap gap-2">
            <Action icon={Download} label="Download" onClick={() => onDownload(c)} testid="creation-mini-download" />
            <Action icon={Share2} label="Share" onClick={() => onShare(c)} testid="creation-mini-share" />
            <Action icon={Trash2} label="Delete" onClick={() => onDelete(c)} testid="creation-mini-delete" danger />
          </div>
          {published ? (
            <button onClick={() => onUnpublish(c)} data-testid="creation-mini-unpublish" className="w-full flex items-center justify-center gap-2 rounded-lg border border-zinc-700 text-zinc-300 font-heading font-bold py-3 hover:border-red-500 hover:text-red-400 transition-colors">
              <EyeOff size={16} /> Unpublish from feed
            </button>
          ) : (
            <button onClick={() => onPublish(c)} data-testid="creation-mini-publish" className="w-full flex items-center justify-center gap-2 rounded-lg bg-gold text-ink-950 font-heading font-black py-3">
              <Send size={16} /> Publish to feed
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function PostDetailModal({ p, isMe, onClose, onDelete, onShare, onToggleFeed }) {
  const isVideo = p.media_type === "video";
  const onFeed = p.to_feed !== false;
  useEffect(() => { const h = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);
  const Action = ({ icon: Icon, label, onClick, testid, danger }) => (
    <button onClick={onClick} data-testid={testid}
      className={`flex flex-col items-center gap-1 flex-1 min-w-[60px] py-2.5 rounded-lg border transition-colors ${danger ? "border-zinc-800 text-zinc-400 hover:border-red-500 hover:text-red-400" : "border-zinc-800 text-zinc-300 hover:border-gold hover:text-gold"}`}>
      <Icon size={17} /><span className="text-[10px] font-heading">{label}</span>
    </button>
  );
  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="post-detail-modal">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-ink-900 border-t sm:border border-zinc-800 sm:rounded-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 sticky top-0 bg-ink-900 z-10">
          <span className="font-heading font-bold">Post</span>
          <button onClick={onClose} data-testid="post-detail-close" className="text-zinc-400"><X size={20} /></button>
        </div>
        <div className="w-full bg-black flex items-center justify-center max-h-[55vh]">
          {p.media_type === "link" ? <div className="w-full"><LinkCard link={p.link} /></div>
            : isVideo ? <video src={p.media_url} controls autoPlay loop playsInline className="w-full max-h-[55vh] object-contain" />
            : <img src={p.media_url} alt="" className="w-full max-h-[55vh] object-contain" />}
        </div>
        <div className="p-4 space-y-4">
          {p.caption && <p className="text-sm text-[#F0E9DD]" data-testid="post-detail-caption">{p.caption}</p>}
          {isMe && (
            <>
              <div className="flex flex-wrap gap-2">
                <Action icon={Share2} label="Share" onClick={() => onShare(p)} testid="post-detail-share" />
                <Action icon={Trash2} label="Delete" onClick={() => onDelete(p)} testid="post-detail-delete" danger />
              </div>
              <button onClick={() => onToggleFeed(p)} data-testid="post-detail-feed-toggle"
                className={`w-full flex items-center justify-center gap-2 rounded-lg font-heading font-bold py-3 transition-colors ${onFeed ? "border border-zinc-700 text-zinc-300 hover:border-red-500 hover:text-red-400" : "bg-gold text-ink-950"}`}>
                {onFeed ? <><EyeOff size={16} /> Remove from feed</> : <><Send size={16} /> Post to feed · 1 coin</>}
              </button>
            </>
          )}
          {!isMe && <button onClick={() => onShare(p)} data-testid="post-detail-share" className="w-full flex items-center justify-center gap-2 rounded-lg border border-zinc-700 text-zinc-200 font-heading font-bold py-3"><Share2 size={16} /> Share</button>}
        </div>
      </div>
    </div>
  );
}
